import * as pc from "playcanvas";
import { RemoteCharacter } from "./RemoteCharacter";

const BASE_URL = "/models/character.glb"; // mesh + skeleton + death clip
/** fbx2gltf already converts Mixamo to metres (model is ~1.8 m), so no
 *  extra scaling is needed — 0.01 here made the body 1.8 cm (invisible). */
const MODEL_SCALE = 1.0;

/** Animation clips (without-skin GLBs) → state name + loop flag. `idle`
 *  is first so it becomes the default state. */
const ANIM_SPECS: ReadonlyArray<{ url: string; state: string; loop: boolean; speed?: number }> = [
  { url: "/models/anim/idle.glb",     state: "idle",     loop: true },
  { url: "/models/anim/run.glb",      state: "run",      loop: true },
  { url: "/models/anim/walk.glb",     state: "walk",     loop: true },
  { url: "/models/anim/run_back.glb", state: "run_back", loop: true },
  { url: "/models/anim/walk_back.glb", state: "walk_back", loop: true },
  { url: "/models/anim/strafe_l.glb", state: "strafe_l", loop: true },
  { url: "/models/anim/strafe_r.glb", state: "strafe_r", loop: true },
  // Reload, one variant per stance/movement (picked at runtime). Sped up
  // so the mag-swap reads within the ~2.2 s reload window.
  { url: "/models/anim/reload_idle.glb",   state: "reload_idle",   loop: false, speed: 1.6 },
  { url: "/models/anim/reload_walk.glb",   state: "reload_walk",   loop: false, speed: 1.6 },
  { url: "/models/anim/reload_run.glb",    state: "reload_run",    loop: false, speed: 1.6 },
  { url: "/models/anim/reload_crouch.glb", state: "reload_crouch", loop: false, speed: 1.6 },
  // Relaxed idle (arms down) for when the knife is out — the default idle
  // is a rifle-aiming pose that looks wrong holding a knife.
  { url: "/models/anim/idle_knife.glb", state: "idle_knife", loop: true },
  { url: "/models/anim/grenade.glb",  state: "grenade",  loop: false },
  // Sped up so the leg-tuck reads within the ~0.7 s air time (clip is ~2.2 s).
  { url: "/models/anim/jump.glb",     state: "jump",     loop: false, speed: 2.0 },
  // Crouch-walk in 4 directions; crouch_idle is derived from crouch_fwd.
  { url: "/models/anim/crouch_fwd.glb",   state: "crouch_fwd",   loop: true },
  { url: "/models/anim/crouch_back.glb",  state: "crouch_back",  loop: true },
  { url: "/models/anim/crouch_left.glb",  state: "crouch_left",  loop: true },
  { url: "/models/anim/crouch_right.glb", state: "crouch_right", loop: true },
  // Match-result poses for the end screen: victory dance loops for as long
  // as the screen is up; the defeat slump plays once and holds its last frame.
  { url: "/models/anim/dance.glb",    state: "dance",    loop: true },
  { url: "/models/anim/defeated.glb", state: "defeated", loop: false },
  // Halftime side-swap poses (random pick per player): the arm stretch loops
  // for the whole cinematic; the stop-walking-with-rifle clip plays once and
  // holds its final stand.
  { url: "/models/anim/stretch.glb",         state: "stretch",         loop: true },
  { url: "/models/anim/rifle_walk_stop.glb", state: "rifle_walk_stop", loop: false },
];

interface ContainerLike {
  instantiateRenderEntity: (opts?: object) => pc.Entity;
  animations?: Array<{ resource?: pc.AnimTrack } | pc.AnimTrack>;
}

/** Pull the first AnimTrack out of a loaded glTF container asset. */
function firstTrack(asset: pc.Asset): pc.AnimTrack | null {
  const res = asset.resource as unknown as ContainerLike;
  const a0 = res.animations?.[0];
  if (!a0) return null;
  return ("resource" in a0 ? a0.resource : (a0 as pc.AnimTrack)) ?? null;
}

/**
 * CharacterFactory — loads the skinned Mixamo character once (the body +
 * skeleton + death clip) plus a set of "without-skin" animation clips, all
 * sharing the same `mixamorig:` skeleton. `create()` stamps out a fully
 * wired, team-tinted character ready to drive via {@link RemoteCharacter}.
 */
export class CharacterFactory {
  private readonly app: pc.AppBase;
  private base: pc.Asset | null = null;
  private readonly anims = new Map<string, { asset: pc.Asset; loop: boolean }>();
  private pending: number;

  constructor(app: pc.AppBase) {
    this.app = app;
    this.pending = 1 + ANIM_SPECS.length;

    this.app.assets.loadFromUrl(BASE_URL, "container", (err, asset) => {
      if (err || !asset) console.warn("[CharacterFactory] base load failed:", BASE_URL, err);
      else this.base = asset;
      this.pending--;
    });

    for (const spec of ANIM_SPECS) {
      this.app.assets.loadFromUrl(spec.url, "container", (err, asset) => {
        if (err || !asset) console.warn("[CharacterFactory] anim load failed:", spec.url, err);
        else this.anims.set(spec.state, { asset, loop: spec.loop });
        this.pending--;
        if (this.pending === 0) console.log("[CharacterFactory] character + animations ready");
      });
    }
  }

  /** True once every load attempt has resolved (base + all clips). The
   *  base may still have failed — `create()` returns null in that case. */
  get ready(): boolean {
    return this.pending === 0;
  }

  /** Instantiate a team-tinted character. Returns null if the base body
   *  failed to load (caller keeps the primitive rig). */
  create(team: "ct" | "t"): RemoteCharacter | null {
    if (!this.base) return null;
    const res = this.base.resource as unknown as ContainerLike;

    const entity = res.instantiateRenderEntity();
    entity.setLocalScale(MODEL_SCALE, MODEL_SCALE, MODEL_SCALE);
    entity.addComponent("anim", { activate: true });
    const anim = entity.anim;
    if (!anim) return null;

    const available = new Set<string>();
    // `idle` first → becomes the default playing state.
    for (const spec of ANIM_SPECS) {
      const entry = this.anims.get(spec.state);
      const track = entry && firstTrack(entry.asset);
      if (track) {
        anim.assignAnimation(spec.state, track, undefined, spec.speed ?? 1, spec.loop);
        available.add(spec.state);
      }
    }
    const deathTrack = firstTrack(this.base);
    if (deathTrack) {
      anim.assignAnimation("death", deathTrack, undefined, 1, false);
      available.add("death");
    }

    // No dedicated crouch-idle clip — hold the crouch-forward clip at frame
    // 0 (speed 0) as a static crouched stance.
    const crouchFwd = this.anims.get("crouch_fwd");
    const crouchTrack = crouchFwd && firstTrack(crouchFwd.asset);
    if (crouchTrack) {
      anim.assignAnimation("crouch_idle", crouchTrack, undefined, 0, false);
      available.add("crouch_idle");
    }

    this.tint(entity, team);
    return new RemoteCharacter(entity, anim, available);
  }

  /** Clone + tint the character's materials so CT/T read by colour. The
   *  base mesh is a flat grey Mixamo character, shared across instances, so
   *  materials must be cloned before tinting to avoid cross-talk. */
  private tint(entity: pc.Entity, team: "ct" | "t"): void {
    const tint = team === "ct"
      ? new pc.Color(0.32, 0.45, 0.72) // CT blue
      : new pc.Color(0.74, 0.42, 0.22); // T orange
    const renders = entity.findComponents("render") as pc.RenderComponent[];
    for (const render of renders) {
      for (const mi of render.meshInstances) {
        const mat = mi.material.clone() as pc.StandardMaterial;
        mat.diffuse = tint;
        mat.update();
        mi.material = mat;
      }
    }
  }
}
